import React from 'react';
// import { useUnmount } from 'react-use';
import axios from '../request/axios';
import { useRefMounted } from './lifecyclesHook';

let cacheMaps = new Map();

export const removeCacheKeys = (key) => {
  if (cacheMaps.has(key)) {
    cacheMaps.delete(key);
  }
};

function requestAPI({ url, params, method = 'get', timeout, baseURL }) {
  //   console.log('axios实例', axios.defaults.baseURL);
  return axios.request({
    url,
    method,
    baseURL: baseURL || axios.defaults.baseURL,
    params: method === 'get' && params,
    data: method === 'post' && params,
    timeout,
  });
}

function requestPageAPI({ url, params, method, baseURL }) {
  return requestAPI({
    url,
    params,
    method,
    baseURL,
  }).then((response) => {
    if (response.code === 0 && response.dataPage) {
      const { pageNo, pageSize, totalCount, totalPages, result } =
        response.dataPage;

      const extendKeys = Object.keys(response).filter(
        (m) => !['code', 'message', 'dataPage'].includes(m),
      );

      let extendsFiled = {};

      if (extendKeys && extendKeys.length > 0) {
        extendsFiled = extendKeys.reduce((extendsObject, current) => {
          return { ...extendsObject, [current]: response[current] };
        }, {});
      }
      return {
        pageNo,
        pageSize,
        totalCount,
        totalPages,
        data: result,
        code: response.code,
        ...extendsFiled,
      };
    } else {
      return {
        pageNo: params.pageNo,
        pageSize: params.pageSize,
        totalCount: 0,
        totalPages: 0,
        data: [],
        code: response.code,
      };
    }
  });
}

function useRequest() {
  const mounted = useRefMounted();

  const [loading, setLoading] = React.useState(false);

  const fetchAPI = React.useCallback(
    ({ url, params, baseURL, method = 'get', timeout }) => {
      setLoading(true);

      return requestAPI({ url, params, baseURL, method: method, timeout })
        .then((response) => {
          return response;
        })
        .finally(() => {
          if (!mounted.current) return;

          setLoading(false);
        });
    },
    [mounted],
  );
  return { fetchAPI, loading };
}

//查询数据 数组
function useFetchDataList({
  url,
  initParams,
  baseURL,
  method = 'get',
  manual = false,
  cacheKey = '',
}) {
  const paramRef = React.useRef(initParams);

  const { loading, fetchAPI } = useRequest();

  const mounted = useRefMounted();

  const [data, setData] = React.useState(() => {
    if (cacheKey && cacheMaps.get(cacheKey)) {
      return cacheMaps.get(cacheKey);
    }
    return { dataList: [], initializing: true };
  });

  const fetchDataCallback = React.useCallback(() => {
    return fetchAPI({ url, params: paramRef.current, baseURL, method }).then(
      (response) => {
        if (!mounted.current) return;

        if (response.code === 0) {
          const extendKeys = Object.keys(response).filter(
            (m) => !['code', 'message', 'dataList'].includes(m),
          );

          let extendsFiled = {};

          if (extendKeys && extendKeys.length > 0) {
            extendsFiled = extendKeys.reduce((extendsObject, current) => {
              return { ...extendsObject, [current]: response[current] };
            }, {});
          }
          setData({
            dataList: response.dataList,
            ...extendsFiled,
            initializing: false,
          });
        } else {
          setData({ dataList: [], initializing: false });
        }
        return response;
      },
    );
  }, [fetchAPI, url, baseURL, method, mounted]);

  const fetchData = React.useCallback(
    (params) => {
      if (params) {
        paramRef.current = params;
      }
      return fetchDataCallback();
    },
    [fetchDataCallback],
  );

  React.useEffect(() => {
    if (cacheKey && cacheMaps.get(cacheKey)) {
      return;
    }

    if (!manual) {
      fetchData();
    }
  }, [manual, fetchData, cacheKey]);

  //   useUnmount(() => {
  //     if (cacheKey) {
  //       cacheMaps.set(cacheKey, data);
  //     }
  //   });

  return { loading, ...data, fetchData, setData };
}

//查询数据
function useFetchData({
  url,
  initParams,
  baseURL,
  method = 'get',
  manual = false,
  cacheKey = '',
}) {
  const paramRef = React.useRef(initParams);

  const mounted = useRefMounted();

  const { loading, fetchAPI } = useRequest();

  const [data, setData] = React.useState(() => {
    if (cacheKey && cacheMaps.get(cacheKey)) {
      return cacheMaps.get(cacheKey);
    }
    return { data: {}, initializing: true };
  });

  const fetchDataCallback = React.useCallback(() => {
    return fetchAPI({ url, params: paramRef.current, baseURL, method }).then(
      (response) => {
        if (!mounted.current) return;

        if (response.code === 0) {
          const extendKeys = Object.keys(response).filter(
            (m) => !['data'].includes(m),
          );

          let extendsFiled = {};

          if (extendKeys && extendKeys.length > 0) {
            extendsFiled = extendKeys.reduce((extendsObject, current) => {
              return { ...extendsObject, [current]: response[current] };
            }, {});
          }

          setData({
            data: response.data || {},
            ...extendsFiled,
            initializing: false,
          });
        } else {
          setData({ data: {}, message: response.message, initializing: false });
        }
        return response;
      },
    );
  }, [fetchAPI, url, baseURL, method, mounted]);

  const fetchData = React.useCallback(
    (params) => {
      if (params) {
        paramRef.current = params;
      }

      return fetchDataCallback();
    },
    [fetchDataCallback],
  );

  React.useEffect(() => {
    if (cacheKey && cacheMaps.get(cacheKey)) {
      return;
    }
    if (!manual) {
      fetchData();
    }
  }, [manual, fetchData, cacheKey]);
  //   useUnmount(() => {
  //     if (cacheKey) {
  //       cacheMaps.set(cacheKey, data);
  //     }
  //   });

  return { loading, ...data, fetchData };
}

function useLoadMore({
  url,
  options = {
    defaultParams: {},
    manual: false,
    limit: 10,
  },
  baseURL,
  method = 'get',
  cacheKey = '',
}) {
  const isMounted = useRefMounted();

  const { defaultParams = {}, manual = false, limit = 10 } = options;

  const paramsRef = React.useRef(defaultParams);

  const [data, setData] = React.useState(() => {
    if (cacheKey && cacheMaps.get(cacheKey)) {
      return cacheMaps.get(cacheKey);
    }
    return {
      data: [],
      loading: false,
      loadingMore: false,
      hasMore: true,
      empty: false,
    };
  });

  const pageRef = React.useRef({
    pageNo: 0,
    pageSize: limit,
  });

  const loadMore = React.useCallback(() => {
    pageRef.current.pageNo++;

    const params = {
      pageNo: pageRef.current.pageNo,
      pageSize: pageRef.current.pageSize,
      ...paramsRef.current,
    };

    setData((draftState) => {
      return { ...draftState, loadingMore: true };
    });

    return requestPageAPI({ url, params, method, baseURL }).then((dataPage) => {
      if (isMounted.current) {
        setData((draftState) => {
          let currentState = { ...draftState };

          if (dataPage.pageNo >= dataPage.totalPages) {
            currentState.hasMore = false;
          }
          currentState.loadingMore = false;

          currentState.data = [...currentState.data, ...dataPage.data];

          const extendKeys = Object.keys(dataPage).filter(
            (m) =>
              ![
                'data',
                'pageNo',
                'pageSize',
                'totalCount',
                'totalPages',
              ].includes(m),
          );

          if (extendKeys) {
            extendKeys.forEach((m) => {
              currentState[m] = dataPage[m];
            });
          }

          return currentState;
        });
      }
      return dataPage;
    });
  }, [url, method, baseURL, isMounted]);

  const load = React.useCallback(
    (params) => {
      if (params) {
        paramsRef.current = { ...paramsRef.current, ...params };
      }
      pageRef.current.pageNo = 1;

      const mergeParams = {
        pageNo: pageRef.current.pageNo,
        pageSize: pageRef.current.pageSize,
        ...paramsRef.current,
      };

      setData((draftState) => {
        return {
          ...draftState,
          loading: true,
          hasMore: true,
          empty: false,
          data: [],
        };
      });

      return requestPageAPI({ url, params: mergeParams, method, baseURL }).then(
        (dataPage) => {
          if (isMounted.current) {
            setData((draftState) => {
              let currentState = { ...draftState };

              if (
                dataPage.data.length === 0 &&
                dataPage.pageNo >= dataPage.totalPages
              ) {
                currentState.empty = true;
              }
              currentState.loading = false;
              currentState.data = dataPage.data;

              if (dataPage.pageNo >= dataPage.totalPages) {
                currentState.hasMore = false;
              }
              const extendKeys = Object.keys(dataPage).filter(
                (m) =>
                  ![
                    'data',
                    'pageNo',
                    'pageSize',
                    'totalCount',
                    'totalPages',
                  ].includes(m),
              );

              if (extendKeys) {
                extendKeys.forEach((m) => {
                  currentState[m] = dataPage[m];
                });
              }

              return currentState;
            });
          }
          return dataPage;
        },
      );
    },
    [url, method, baseURL, isMounted],
  );
  //更新数据
  const refresh = React.useCallback(
    (params) => {
      if (params) {
        paramsRef.current = { ...paramsRef.current, ...params };
      }
      pageRef.current.pageNo = 1;

      const mergeParams = {
        needToast: true,
        pageNo: pageRef.current.pageNo,
        pageSize: pageRef.current.pageSize,
        ...paramsRef.current,
      };

      let start = new Date();

      return requestPageAPI({ url, params: mergeParams, method, baseURL })
        .then((dataPage) => {
          let interval = new Date() - start;

          return new Promise((resolve) => {
            if (interval < 1000) {
              setTimeout(() => {
                resolve(dataPage);
              }, 1000 - interval);
            } else {
              resolve(dataPage);
            }
          });
        })
        .then((dataPage) => {
          if (isMounted.current) {
            setData((draftState) => {
              let currentState = { ...draftState };

              if (
                dataPage.data.length === 0 &&
                dataPage.pageNo >= dataPage.totalPages
              ) {
                currentState.empty = true;
              } else {
                currentState.empty = false;
              }
              currentState.loading = false;
              currentState.data = dataPage.data;

              if (dataPage.pageNo >= dataPage.totalPages) {
                currentState.hasMore = false;
              } else {
                currentState.hasMore = true;
              }
              const extendKeys = Object.keys(dataPage).filter(
                (m) =>
                  ![
                    'data',
                    'pageNo',
                    'pageSize',
                    'totalCount',
                    'totalPages',
                  ].includes(m),
              );

              if (extendKeys) {
                extendKeys.forEach((m) => {
                  currentState[m] = dataPage[m];
                });
              }

              return currentState;
            });
          }
          return dataPage;
        });
    },
    [url, method, baseURL, isMounted],
  );

  const saveCache = React.useCallback((cacheKey) => {
    cacheMaps.set(`${cacheKey}-page`, pageRef.current.pageNo);
  }, []);

  const resetCache = React.useCallback((cacheKey, data) => {
    cacheMaps.set(cacheKey, data);

    setData((m) => {
      return { ...m, data: data };
    });
  }, []);

  const updateItem = React.useCallback(
    (key, newItemData) => {
      console.log(key, newItemData, '==');
      setData((draftState) => {
        let currentState = { ...draftState };

        // 找到要更新的项的索引
        const itemIndex = currentState.data.findIndex(
          (item) => item[key] === newItemData[key],
        );
        if (itemIndex === -1) {
          // 如果找不到项，可以选择在这里处理错误或者直接返回
          console.error('Item not found');
          return currentState;
        }

        // 更新数据项
        currentState.data = [
          ...currentState.data.slice(0, itemIndex),
          newItemData,
          ...currentState.data.slice(itemIndex + 1),
        ];

        return currentState;
      });
    },
    [setData],
  );

  React.useEffect(() => {
    if (
      cacheKey &&
      cacheMaps.get(cacheKey) &&
      cacheMaps.get(cacheKey).data.length > 0
    ) {
      return;
    }
    if (!manual) {
      load();
    }
  }, [load, manual, cacheKey]);

  //   useUnmount(() => {
  //     if (cacheKey) {
  //       cacheMaps.set(cacheKey, data);
  //     }
  //   });

  return {
    ...data,
    setData,
    loadMore,
    load,
    refresh,
    saveCache,
    resetCache,
    updateItem,
  };
}

export { requestAPI, useFetchData, useFetchDataList, useLoadMore, useRequest };
